import React from 'react';
import { useSwitch, type AriaSwitchProps } from '@react-aria/switch';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useToggleState } from '@react-stately/toggle';
import { cn } from '../cn';

type SwitchProps = Omit<AriaSwitchProps, 'isDisabled' | 'isSelected'> & {
  /**
   * Applies a recommended label.
   */
  label?: string,
  /**
   * Sets the selected status of the switch.
   */
  checked?: boolean,
  /**
   * Disables interaction with the switch.
   */
  disabled?: boolean,
  /**
   * Applies an optional color the the switch track.
   */
  color?: 'primary' | 'success' | 'warning' | 'error',
  disabledColor?: string,
  /**
   * Function that is called when the switch has been toggled.
   */
  onChange?: () => void,
  className?: string,
  labelClassName?: string,
}

const Switch = ({
  label,
  checked = false,
  disabled = false,
  color = 'success',
  disabledColor = 'bg-neutral-200',
  onChange,
  className,
  labelClassName,
  excludeFromTabOrder,
  ...otherProps
}: SwitchProps): JSX.Element => {
  const ref = React.useRef(null);
  const switchState = useToggleState({ isSelected: checked, onChange });
  const { inputProps, labelProps, isDisabled, isSelected } = useSwitch(
    { isSelected: checked, isDisabled: disabled, children: label, onChange },
    switchState,
    ref
  );

  const colorMap = {
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
  };

  return (
    <label
      className={cn([
        'py-2 group',
        'text-text',
        !label && 'w-fit',
        'flex justify-between items-center',
        'disabled:opacity-50 diabled:cursor-not-allowed',
        'cursor-pointer',
        className,
      ])}
      {...labelProps}
    >
      <VisuallyHidden>
        <input
          ref={ref}
          className={cn(['hidden'])}
          type='checkbox'
          onChange={onChange}
          disabled={isDisabled}
          {...inputProps}
          {...otherProps}
          tabIndex={excludeFromTabOrder && -1 || undefined}
        />
      </VisuallyHidden>
      {label && (
        <span className={cn([
          'text-text-lighter',
          'pb-0.5',
          'leading-3 font-semibold',
          labelClassName,
        ])}>{label}</span>
      )}
      <div
        className={cn([
          'p-1',
          'm-w-12 w-12',
          'flex items-center',
          'rounded-lg',
          !isSelected ? `${disabledColor} justify-start` : `${colorMap[color]} justify-end`,
        ])}
      >
        <div
          className={cn([
            'w-4 h-4',
            'bg-surface',
            'rounded-full',
          ])}>
        </div>
      </div>
    </label>
  );
};

export default Switch;
